// Ubicación en el repo: src/components/ExamDetailModal.jsx
// "Ver audiograma" — mismo contenido que openExamDetail() del prototipo
// (línea ~2060): audiograma, promedio tonal puro (PTA), diapasones (si
// hay datos), logoaudiometría + curva (si hay datos), notas.
import { useState } from 'react';
import { calcPTA, ptaLabel } from '../lib/exams';
import { formatPatientName, formatProfessionalName, formatDate } from '../lib/format';
import { printExamReport } from '../lib/examReport';
import { sendExamReportEmail } from '../lib/examEmail';
import { getPatientBranchLogoUrl } from '../lib/branchLogos';
import { getProfessionalSignatureUrl } from '../lib/professionalSignatures';
import AudiogramSVG from './AudiogramSVG';
import SpeechCurveSVG from './SpeechCurveSVG';

export default function ExamDetailModal({ exam, activeBranchId, onClose, onEdit }) {
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  // Advertencia previa al envío (22-sep-2026, pedido de Nicolás): antes de
  // mandarle el informe al paciente, el profesional tiene que confirmar
  // explícitamente que los datos están correctos — "SI"/"Revisar antes de
  // enviar" en vez de mandar directo al clic del botón, para evitar que se
  // le llegue a enviar un examen con un error sin haberlo revisado primero.
  const [showSendConfirm, setShowSendConfirm] = useState(false);

  async function handleSendEmail() {
    setShowSendConfirm(false);
    setSendingEmail(true);
    setEmailError('');
    setEmailSent(false);
    try {
      const [logoUrl, signatureUrl] = await Promise.all([
        getPatientBranchLogoUrl(exam.patients, activeBranchId),
        getProfessionalSignatureUrl(exam.professionals),
      ]);
      await sendExamReportEmail(exam, logoUrl, signatureUrl);
      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message || 'No se pudo enviar el correo.');
    } finally {
      setSendingEmail(false);
    }
  }

  const ptaMode = exam.pta_mode ?? 4;
  const ptaAirOd = calcPTA(exam.od, ptaMode, exam.od_nr);
  const ptaAirOi = calcPTA(exam.oi, ptaMode, exam.oi_nr);
  const ptaBoneOd = calcPTA(exam.bone_od, ptaMode, exam.bone_od_nr);
  const ptaBoneOi = calcPTA(exam.bone_oi, ptaMode, exam.bone_oi_nr);

  const r = exam.rinne || {};
  const w = exam.weber || {};
  const hasDiapasones = r.od250 || r.oi250 || r.od500 || r.oi500 || w.od250 || w.oi250 || w.od500 || w.oi500;

  const logoOd = exam.logo_od || {};
  const logoOi = exam.logo_oi || {};
  const hasLogo = (logoOd.percent != null && logoOd.db != null) || (logoOi.percent != null && logoOi.db != null);
  const hasLogoData = hasLogo || logoOd.masking != null || logoOi.masking != null;

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>Audiometría · {formatPatientName(exam.patients)}</h2>
        <p className="field-hint" style={{ marginTop: -6 }}>
          {formatDate(exam.date)}
          {exam.professionals ? ` · ${formatProfessionalName(exam.professionals, { withRole: true })}` : ''}
          {exam.equipment ? ` · ${exam.equipment}` : ''}
        </p>

        <div className="audiogram-wrap">
          <AudiogramSVG
            od={exam.od}
            oi={exam.oi}
            boneOd={exam.bone_od}
            boneOi={exam.bone_oi}
            ldlOd={exam.ldl_od}
            ldlOi={exam.ldl_oi}
            odMasked={exam.od_masked}
            oiMasked={exam.oi_masked}
            boneOdMasked={exam.bone_od_masked}
            boneOiMasked={exam.bone_oi_masked}
            odNr={exam.od_nr}
            oiNr={exam.oi_nr}
            boneOdNr={exam.bone_od_nr}
            boneOiNr={exam.bone_oi_nr}
          />
          <div className="legend">
            <span>
              <span className="dot" style={{ background: '#C4453C' }} />
              OD: ○ aérea (△ enmascarada) · &lt; ósea ([ enmascarada) · ▷ LDL
            </span>
            <span>
              <span className="dot" style={{ background: '#2563A6' }} />
              OI: × aérea (□ enmascarada) · &gt; ósea (] enmascarada) · ◁ LDL
            </span>
          </div>
        </div>

        <table className="detail-table">
          <thead>
            <tr>
              <th>Promedio {ptaLabel(ptaMode)} Hz (PTP)</th>
              <th className="freq-table__od">OD</th>
              <th className="freq-table__oi">OI</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Vía aérea</td>
              <td className="freq-table__od" style={{ fontWeight: 700 }}>
                {ptaAirOd !== null ? `${ptaAirOd} dB` : 'No calculable'}
              </td>
              <td className="freq-table__oi" style={{ fontWeight: 700 }}>
                {ptaAirOi !== null ? `${ptaAirOi} dB` : 'No calculable'}
              </td>
            </tr>
            <tr>
              <td>Vía ósea</td>
              <td className="freq-table__od" style={{ fontWeight: 700 }}>
                {ptaBoneOd !== null ? `${ptaBoneOd} dB` : 'No calculable'}
              </td>
              <td className="freq-table__oi" style={{ fontWeight: 700 }}>
                {ptaBoneOi !== null ? `${ptaBoneOi} dB` : 'No calculable'}
              </td>
            </tr>
          </tbody>
        </table>

        {hasDiapasones && (
          <>
            <h4 style={{ marginTop: 16, fontSize: 13 }}>Diapasones</h4>
            <table className="detail-table">
              <thead>
                <tr>
                  <th></th>
                  <th className="freq-table__od">Rinne OD</th>
                  <th className="freq-table__oi">Rinne OI</th>
                  <th className="freq-table__od">Weber OD</th>
                  <th className="freq-table__oi">Weber OI</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>250 Hz</td>
                  <td>{r.od250 || 'No realizado'}</td>
                  <td>{r.oi250 || 'No realizado'}</td>
                  <td>{w.od250 || 'No realizado'}</td>
                  <td>{w.oi250 || 'No realizado'}</td>
                </tr>
                <tr>
                  <td>500 Hz</td>
                  <td>{r.od500 || 'No realizado'}</td>
                  <td>{r.oi500 || 'No realizado'}</td>
                  <td>{w.od500 || 'No realizado'}</td>
                  <td>{w.oi500 || 'No realizado'}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {hasLogoData && (
          <>
            <h4 style={{ marginTop: 18, fontSize: 13 }}>
              Logoaudiometría{exam.logo_test_type ? ` · ${exam.logo_test_type}` : ''}
            </h4>
            <table className="detail-table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Discriminación</th>
                  <th>%</th>
                  <th>dB</th>
                  <th>MKG</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="freq-table__od" style={{ fontWeight: 700 }}>
                    OD
                  </td>
                  <td className="freq-table__od" style={{ fontWeight: 600 }}>
                    {logoOd.percent != null ? `${logoOd.percent}%` : '—'}
                  </td>
                  <td className="freq-table__od" style={{ fontWeight: 600 }}>
                    {logoOd.db != null ? `${logoOd.db} dB` : '—'}
                  </td>
                  <td className="freq-table__oi" style={{ fontWeight: 600 }}>
                    {logoOd.masking != null ? `${logoOd.masking} dB` : '—'}
                  </td>
                </tr>
                <tr>
                  <td className="freq-table__oi" style={{ fontWeight: 700 }}>
                    OI
                  </td>
                  <td className="freq-table__oi" style={{ fontWeight: 600 }}>
                    {logoOi.percent != null ? `${logoOi.percent}%` : '—'}
                  </td>
                  <td className="freq-table__oi" style={{ fontWeight: 600 }}>
                    {logoOi.db != null ? `${logoOi.db} dB` : '—'}
                  </td>
                  <td className="freq-table__od" style={{ fontWeight: 600 }}>
                    {logoOi.masking != null ? `${logoOi.masking} dB` : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="field-hint" style={{ margin: '-6px 0 10px' }}>
              El MKG se muestra en el color del oído contralateral (el que recibe el enmascaramiento).
            </p>
            {hasLogo && (
              <div className="audiogram-wrap">
                <SpeechCurveSVG logoOd={logoOd} logoOi={logoOi} />
                <div className="legend">
                  <span>
                    <span className="dot" style={{ background: '#C4453C' }} />
                    OD
                  </span>
                  <span>
                    <span className="dot" style={{ background: '#2563A6' }} />
                    OI
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {exam.notes && <p style={{ marginTop: 14 }}>{exam.notes}</p>}

        {emailError && (
          <p className="auth-error" style={{ marginTop: 10 }}>
            {emailError}
          </p>
        )}
        {emailSent && !emailError && (
          <p className="field-hint" style={{ marginTop: 10 }}>
            Correo enviado a {exam.patients?.email}.
          </p>
        )}

        {showSendConfirm && (
          <div className="card-panel" style={{ marginTop: 14, borderColor: '#c7891f' }}>
            <p style={{ margin: '0 0 8px', fontWeight: 700 }}>
              ¿Confirmas que esta audiometría está correcta antes de enviarla?
            </p>
            <p className="field-hint" style={{ marginBottom: 14 }}>
              Revisa el audiograma, el PTP y las observaciones en esta misma pantalla — una vez enviado, el
              paciente recibe el informe tal como se ve acá.
            </p>
            <div className="modal-actions" style={{ marginTop: 0 }}>
              <button type="button" className="button-secondary" onClick={() => setShowSendConfirm(false)}>
                Revisar antes de enviar
              </button>
              <button type="button" className="button-primary" onClick={handleSendEmail}>
                Sí, enviar
              </button>
            </div>
          </div>
        )}

        {!showSendConfirm && (
          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose}>
              Cerrar
            </button>
            <button
              type="button"
              className="button-secondary"
              onClick={async () => {
                const [logoUrl, signatureUrl] = await Promise.all([
                  getPatientBranchLogoUrl(exam.patients, activeBranchId),
                  getProfessionalSignatureUrl(exam.professionals),
                ]);
                printExamReport(exam, logoUrl, signatureUrl);
              }}
            >
              🖨 Imprimir informe
            </button>
            <button
              type="button"
              className="button-secondary"
              onClick={() => setShowSendConfirm(true)}
              disabled={sendingEmail || !exam.patients?.email}
              title={!exam.patients?.email ? 'El paciente no tiene correo registrado en su ficha.' : undefined}
            >
              {sendingEmail ? 'Enviando…' : '✉️ Enviar por correo'}
            </button>
            <button type="button" className="button-primary" onClick={onEdit}>
              Editar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
